import {
  getGetInstrumentQuoteQueryKey,
  useGetInstrumentQuote,
} from '@biasafe.queries/portfolio-instruments-conid-quote/portfolio-instruments-conid-quote';
import { ArrowUpRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SearchSecurityTypeChip } from './SearchTypeChip';
import type { SearchItem } from '../model/searchModel';
import { Button } from '@/components/ui/button';
import { QuoteStateBadge } from '@/features/Execution/shared/QuoteStateBadge';
import { useDocumentVisible } from '@/features/Execution/shared/freshness';
import {
  instrumentExchange,
  instrumentVenue,
} from '@/features/Instruments/instrumentMetadata';
import { AssetLogo } from '@/components/tables/tableParts';
import { DirectoryAvatar } from '@/features/Clients/components/DirectoryAvatar';

export function SearchPreview({
  item,
  onClose,
  onOpen,
}: {
  item: SearchItem;
  onClose: () => void;
  onOpen: () => void;
}) {
  const { t } = useTranslation('app-shell');
  const text = (key: string, fallback: string) =>
    t(`header.${key}`, { defaultValue: fallback });
  const visible = useDocumentVisible();
  const conid = item.kind === 'security' ? item.instrument.conid : undefined;
  const quote = useGetInstrumentQuote(conid ?? 0, {
    query: {
      queryKey: getGetInstrumentQuoteQueryKey(conid ?? 0),
      enabled: !!conid,
      refetchInterval: visible ? 5000 : false,
      retry: false,
    },
  });
  const q = quote.data;
  const usable =
    !quote.isError &&
    q &&
    !q.error &&
    q.isSandbox === false &&
    q.quoteState &&
    q.quoteState !== 'unknown' &&
    q.symbol.toUpperCase() === item.code.toUpperCase() &&
    q.currency &&
    /^[A-Z]{3}$/.test(q.currency) &&
    q.asOf &&
    Number.isFinite(Date.parse(q.asOf)) &&
    Number.isFinite(q.last) &&
    q.last > 0 &&
    (!q.conid || q.conid === conid);
  return (
    <aside
      aria-label={text('search_preview', 'Result preview')}
      className="grid max-h-[40dvh] shrink-0 items-center gap-3 overflow-y-auto border-t border-border/50 bg-muted/15 p-3 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:gap-5 sm:px-4"
    >
      <div className="flex min-w-0 items-center gap-3">
        {item.kind === 'security' && (
          <AssetLogo
            ticker={item.code}
            mic={instrumentVenue(item.instrument)?.mic}
            size="sm"
            className="rounded-none p-0"
          />
        )}
        {item.kind === 'client' && (
          <DirectoryAvatar client={item.client} shape="square" />
        )}
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground">
            {text('search_preview', 'Result preview')}
          </p>
          <h3 className="mt-0.5 truncate text-sm font-medium">{item.label}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {item.code}
            {item.kind === 'security' &&
              ` · ${instrumentExchange(item.instrument) || '—'}`}
            {item.kind === 'security' && (
              <SearchSecurityTypeChip type={item.instrument.securityType} />
            )}
          </p>
        </div>
      </div>
      {item.kind === 'security' && (
        <div role="status" className="min-w-0 sm:max-w-64">
          {usable ? (
            <>
              <p className="text-lg font-semibold tabular-nums leading-6">
                {new Intl.NumberFormat(undefined, {
                  style: 'currency',
                  currency: q.currency!,
                  maximumFractionDigits: 4,
                }).format(q.last)}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span>IBKR{q.quoteState === 'live' ? ' · Live' : ''}</span>
                <QuoteStateBadge quote={q} />
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {quote.isPending
                ? text('search_quote_loading', 'Loading IBKR quote…')
                : text(
                    'search_quote_unavailable',
                    'IBKR quote unavailable for this listing.',
                  )}
            </p>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 sm:col-start-3 sm:justify-end">
        <Button
          size="sm"
          variant="outline"
          className="h-8 rounded px-2.5 text-xs"
          onClick={onOpen}
        >
          {text('search_open_result', 'Open result')}
          <ArrowUpRight className="size-4" />
        </Button>
        <Button
          size="icon"
          variant="ghost"
          className="size-7 rounded"
          aria-label={text('search_close_preview', 'Close preview')}
          onClick={onClose}
        >
          <X className="size-3.5" />
        </Button>
      </div>
    </aside>
  );
}
